import React, { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { usePlatformAuth } from "context/PlatformAuthContext";
import {
 FaChartBar,
 FaStore,
 FaCreditCard,
 FaFileInvoice,
 FaShieldAlt,
 FaPlug,
 FaFileAlt,
 FaEnvelope,
 FaBars,
 FaTimes,
 FaChevronDown,
 FaChevronRight,
 FaList,
 FaExternalLinkAlt,
 FaExclamationTriangle,
} from "react-icons/fa";

interface PlatformAdminSidebarProps {
 isCollapsed: boolean;
 onToggle: () => void;
}

interface MenuItem {
 path: string;
 label: string;
 icon: React.ReactNode;
 children?: MenuItem[];
}

const PlatformAdminSidebar: React.FC<PlatformAdminSidebarProps> = React.memo(({ isCollapsed, onToggle }) => {
 const { isAuthenticated } = usePlatformAuth();
 const location = useLocation();
 const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());
 const [isMobile, setIsMobile] = useState(false);

 useEffect(() => {
  const checkMobile = () => setIsMobile(window.innerWidth < 1024);
  checkMobile();
  window.addEventListener("resize", checkMobile);
  return () => window.removeEventListener("resize", checkMobile);
 }, []);

  // Auto-open menu if current path matches - memoize the path check
  const currentPath = location.pathname;
  useEffect(() => {
   const newOpenMenus = new Set<string>();
   
   // Check against original menu structure (before flattening)
   if (currentPath.startsWith("/platform/stores")) newOpenMenus.add("stores");
   if (currentPath.startsWith("/platform/subscriptions") || currentPath.startsWith("/platform/subscription-requests") || currentPath.startsWith("/platform/invoices") || currentPath.startsWith("/platform/plans")) newOpenMenus.add("billing");
   if (currentPath.startsWith("/platform/plugins")) newOpenMenus.add("plugins");
   if (currentPath.startsWith("/platform/pages")) newOpenMenus.add("content");
   if (currentPath.startsWith("/platform/audit-logs") || currentPath.startsWith("/platform/breaches")) newOpenMenus.add("security");
   // Note: "support" is now flattened, so it doesn't need to be in openMenus
   
   setOpenMenus(newOpenMenus);
  }, [currentPath]);

 const toggleMenu = useCallback((menuKey: string) => {
  setOpenMenus((prev) => {
   const next = new Set(prev);
   if (next.has(menuKey)) {
    next.delete(menuKey);
   } else {
    next.add(menuKey);
   }
   return next;
  });
 }, []);

 // Flatten menu items: if a menu item has only one child, use the child's path and label directly
 const menuItems: MenuItem[] = useMemo(() => {
  const rawItems: MenuItem[] = [
   {
    path: "/platform/dashboard",
    label: "Dashboard",
    icon: <FaChartBar className="text-lg" />,
   },
   {
    path: "/platform/stores",
    label: "Stores",
    icon: <FaStore className="text-lg" />,
    children: [
     { path: "/platform/stores", label: "All Stores", icon: <FaList className="text-sm" /> },
     { path: "/platform/stores/new", label: "Create Store", icon: <FaStore className="text-sm" /> },
    ],
   },
   {
    path: "/platform/subscriptions",
    label: "Billing",
    icon: <FaCreditCard className="text-lg" />,
    children: [
     { path: "/platform/subscriptions", label: "Subscriptions", icon: <FaCreditCard className="text-sm" /> },
     { path: "/platform/subscription-requests", label: "Subscription Requests", icon: <FaExclamationTriangle className="text-sm" /> },
     { path: "/platform/invoices", label: "Invoices", icon: <FaFileInvoice className="text-sm" /> },
     { path: "/platform/plans", label: "Plans", icon: <FaFileAlt className="text-sm" /> },
    ],
   },
   {
    path: "/platform/plugins",
    label: "Plugins",
    icon: <FaPlug className="text-lg" />,
    children: [
     { path: "/platform/plugins", label: "All Plugins", icon: <FaList className="text-sm" /> },
     { path: "/platform/plugins/new", label: "Create Plugin", icon: <FaPlug className="text-sm" /> },
    ],
   },
   {
    path: "/platform/pages",
    label: "Content",
    icon: <FaFileAlt className="text-lg" />,
    children: [
     { path: "/platform/pages", label: "Platform Pages", icon: <FaFileAlt className="text-sm" /> },
     { path: "/platform/pages/new", label: "Create Page", icon: <FaFileAlt className="text-sm" /> },
    ],
   },
   {
    path: "/platform/contact-messages",
    label: "Support",
    icon: <FaEnvelope className="text-lg" />,
    children: [
     { path: "/platform/contact-messages", label: "Contact Messages", icon: <FaEnvelope className="text-sm" /> },
    ],
   },
   {
    path: "/platform/audit-logs",
    label: "Security",
    icon: <FaShieldAlt className="text-lg" />,
    children: [
     { path: "/platform/audit-logs", label: "Audit Logs", icon: <FaShieldAlt className="text-sm" /> },
     { path: "/platform/breaches", label: "Breaches", icon: <FaExclamationTriangle className="text-sm" /> },
    ],
   },
  ];

  // Flatten items with single child: replace parent with child
  return rawItems.map((item) => {
   if (item.children && item.children.length === 1) {
    const child = item.children[0];
    return {
     path: child.path,
     label: item.label, // Keep parent label for better UX
     icon: item.icon,
    };
   }
   return item;
  });
 }, []);

 const navLinkClassName = useCallback(({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 px-4 py-3 transition-colors ${
   isActive
    ? "bg-blue-600 text-white"
    : "text-gray-700 hover:bg-gray-100"
  } ${isCollapsed && !isMobile ? "justify-center" : ""}`, [isCollapsed, isMobile]);

 const isMenuOpen = (menuKey: string) => openMenus.has(menuKey);

 // Get platform site URL (root of current domain)
 const getPlatformSiteUrl = (): string => {
  if (typeof window !== "undefined") {
   return window.location.origin;
  }
  return "/";
 };

 if (!isAuthenticated) {
  return null;
 }

 return (
  <aside
   className={`fixed left-0 top-0 h-screen bg-white border-r border-gray-200 z-40 flex flex-col transition-all duration-300 ${
    isCollapsed ? "w-20" : "w-64"
   } hidden lg:flex`}
  >
   {/* Header */}
   <div className={`p-6 pb-4 border-b border-gray-200 flex-shrink-0 ${isCollapsed ? "px-4" : ""}`}>
    <div className="flex items-center justify-between">
     {!isCollapsed && (
      <div>
       <h2 className="text-xl font-bold text-gray-900">Platform Admin</h2>
       <p className="text-xs text-gray-500 mt-1">Management Portal</p>
      </div>
     )}
     <button
      onClick={onToggle}
      className="p-2 hover:bg-gray-100 transition-colors flex-shrink-0"
      aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
     >
      {isCollapsed ? (
       <FaBars className="text-gray-600 text-xl" />
      ) : (
       <FaTimes className="text-gray-600 text-xl" />
      )}
     </button>
    </div>
    {/* Quick Link to Platform Site */}
    <a
     href={getPlatformSiteUrl()}
     target="_blank"
     rel="noopener noreferrer"
     className={`mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-blue-50 rounded-md transition-colors ${
      isCollapsed && !isMobile ? "justify-center" : ""
     }`}
     title={isCollapsed ? "Visit Platform Site" : ""}
    >
     <FaExternalLinkAlt className="text-base flex-shrink-0" />
     {!isCollapsed && <span>Visit Platform Site</span>}
    </a>
   </div>

   {/* Navigation */}
   <nav className="flex-1 overflow-y-auto overflow-x-hidden pt-4">
    <div className="space-y-1 px-2">
     {menuItems.map((item) => {
      const hasChildren = item.children && item.children.length > 0;
      // Extract menu key from path (e.g., "/platform/audit-logs" -> "audit-logs")
      const menuKey = item.path.replace("/platform/", "").split("/")[0] || item.path;
      const isOpen = isMenuOpen(menuKey);

      if (hasChildren) {
       return (
        <div key={item.path}>
         <button
          onClick={(e) => {
           e.preventDefault();
           e.stopPropagation();
           toggleMenu(menuKey);
          }}
          className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
           location.pathname.startsWith(item.path)
            ? "bg-blue-50 text-indigo-700"
            : "text-gray-700 hover:bg-gray-100"
          } ${isCollapsed && !isMobile ? "justify-center" : ""}`}
         >
          <div className="flex items-center gap-3">
           {item.icon}
           {!isCollapsed && <span className="font-medium">{item.label}</span>}
          </div>
          {!isCollapsed && (
           <span className="transition-transform">
            {isOpen ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
           </span>
          )}
         </button>
         {isOpen && !isCollapsed && (
          <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
           {item.children?.map((child) => (
            <NavLink
             key={child.path}
             to={child.path}
             onClick={(e) => {
              // Prevent event bubbling to parent button
              e.stopPropagation();
             }}
             className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
               isActive
                ? "bg-blue-600 text-white"
                : "text-gray-600 hover:bg-gray-100"
              }`
             }
            >
             {child.icon}
             <span>{child.label}</span>
            </NavLink>
           ))}
          </div>
         )}
        </div>
       );
      }

      return (
       <NavLink
        key={item.path}
        to={item.path}
        className={navLinkClassName}
        title={isCollapsed ? item.label : ""}
       >
        {item.icon}
        {!isCollapsed && <span className="font-medium">{item.label}</span>}
       </NavLink>
      );
     })}
    </div>
   </nav>
  </aside>
 );
});

PlatformAdminSidebar.displayName = "PlatformAdminSidebar";

export default PlatformAdminSidebar;

